<template>
  <div class="baumliste-editor">
    <p v-if="!rows.length" class="baumliste-editor__empty">Keine Bäume erfasst.</p>

    <div v-for="(row, index) in rows" :key="index" class="baumliste-editor__row">
      <div class="baumliste-editor__row-fields">
        <div v-for="key in rowKeys(row)" :key="key" class="field">
          <label class="field__label">{{ prettyKey(key) }}</label>
          <input
            type="text"
            class="field__input"
            :value="row[key]"
            @input="updateField(index, key, $event.target.value)"
          />
        </div>
      </div>
      <button type="button" class="baumliste-editor__remove" @click="removeRow(index)">✕ Entfernen</button>
    </div>

    <button type="button" class="baumliste-editor__add" @click="addRow">+ Baum hinzufügen</button>
  </div>
</template>

<script>
import { computed } from 'vue';

// Known field names the FormIQ3 model may emit for a tree entry (Baeume are
// unstructured JSON objects - the extraction schema doesn't fix their keys).
const PRETTY_LABELS = {
  nummer: 'Plakettennummer',
  plakettennummer: 'Plakettennummer',
  plakettennr: 'Plakettennummer',
  baumart: 'Baumart',
  stammumfang: 'Stammumfang',
  massnahme: 'Maßnahme',
  anzahl_stamm: 'Stammart',
  ersatzpflanzung: 'Ersatzpflanzung',
  lage: 'Lage auf dem Grundstück',
  zustand: 'Zustand',
  details: 'Details',
  anmerkung: 'Anmerkung',
  weitereAngaben: 'Weitere Angaben',
};

function prettyKey(key) {
  if (PRETTY_LABELS[key]) return PRETTY_LABELS[key];
  return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
}

// FormIQ3 occasionally emits each tree as a JSON-encoded string instead of an
// object (model inconsistency) - Object.keys() on a string returns one index
// per character, which renders as garbled single-character fields. Parse
// string rows back into objects so the editor always works on real objects.
function normalizeRow(row) {
  if (row && typeof row === 'object' && !Array.isArray(row)) return row;
  if (typeof row === 'string') {
    try {
      const parsed = JSON.parse(row);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    } catch (e) {
      // not valid JSON - fall through to the generic wrapper below
    }
  }
  return { wert: row };
}

export default {
  name: 'BaumlisteEditor',
  props: {
    modelValue: { type: Array, default: () => [] },
    defaultKeys: { type: Array, default: () => ['nummer', 'baumart', 'anmerkung'] },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const rows = computed(() => (props.modelValue || []).map(normalizeRow));

    const rowKeys = (row) => {
      const keys = Object.keys(row || {});
      return keys.length ? keys : props.defaultKeys;
    };

    const updateField = (index, key, value) => {
      const next = rows.value.map((row, i) => (i === index ? { ...row, [key]: value } : row));
      emit('update:modelValue', next);
    };

    const addRow = () => {
      const blank = {};
      props.defaultKeys.forEach((key) => { blank[key] = ''; });
      emit('update:modelValue', [...rows.value, blank]);
    };

    const removeRow = (index) => {
      emit('update:modelValue', rows.value.filter((_, i) => i !== index));
    };

    return { rows, rowKeys, prettyKey, updateField, addRow, removeRow };
  },
};
</script>

<style scoped src="../forms/form.css"></style>
<style scoped>
.baumliste-editor { display: flex; flex-direction: column; gap: 12px; }

.baumliste-editor__empty { font-size: 13px; color: #9ca3af; }

.baumliste-editor__row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px 14px;
}

.baumliste-editor__row-fields {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.baumliste-editor__row-fields .field { min-width: 160px; flex: 1; }

.baumliste-editor__remove {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  font-size: 12px;
  white-space: nowrap;
  margin-top: 6px;
}
.baumliste-editor__remove:hover { color: #c0121c; }

.baumliste-editor__add {
  align-self: flex-start;
  background: #f1f5f9;
  border: 1px solid #d0d7de;
  color: #1e293b;
  padding: 7px 14px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.baumliste-editor__add:hover { background: #e2e8f0; }
</style>
